// Kanemori Helipad (world.json `helipad`): the corporate rooftop pad in Nishi-Shinjuku east. Presentation only: the pad is
// painted over the plaza ground, the low fence ring is drawn as chain-link, and a windsock and beacon stand on the pad.
import { canvas } from './canvas';
import { HT, K, type G, px, speckle, disc, shadow, dk, lt, mix, alpha } from './hd-kit';
import type { LightFn } from './hd-registry';

/** the pad slab, painted tile-aligned from (x, y) = the pad's top-left in hd px; w, h in hd px; ramp = x of the south gap */
export function helipadPad(g: G, x: number, y: number, w: number, h: number, rampX: number, rampW: number, L: LightFn) {
  const slab = dk(K.asphalt, 0.2);
  px(g, x, y, w, h, slab); speckle(g, x, y, w, h, K.asphaltHi, 0.05, 41); speckle(g, x, y, w, h, K.ink, 0.06, 42);
  for (let i = HT; i < w; i += HT) px(g, x + i, y, 1, h, alpha(K.ink, 0.5)); // panel seams
  for (let j = HT; j < h; j += HT) px(g, x, y + j, w, 1, alpha(K.ink, 0.5));
  // safety edge: a yellow line with a dashed inner line
  const e = 8; px(g, x + e, y + e, w - 2 * e, 3, K.warm); px(g, x + e, y + h - e - 3, w - 2 * e, 3, K.warm);
  px(g, x + e, y + e, 3, h - 2 * e, K.warm); px(g, x + w - e - 3, y + e, 3, h - 2 * e, K.warm);
  for (let i = 24; i < w - 24; i += 16) { px(g, x + i, y + e + 7, 8, 2, K.warmDim); px(g, x + i, y + h - e - 9, 8, 2, K.warmDim); }
  for (let j = 24; j < h - 24; j += 16) { px(g, x + e + 7, y + j, 2, 8, K.warmDim); px(g, x + w - e - 9, y + j, 2, 8, K.warmDim); }
  // touchdown circle: white ring, dark inside, a lit amber ring of inset lights
  const cx = x + w / 2, cy = y + h / 2 - 8, R = 136;
  disc(g, cx, cy + 6, R + 4, R, alpha(K.night, 0.5)); // soft shadow of the ring on the slab
  disc(g, cx, cy, R, R, K.paint); disc(g, cx, cy, R - 7, R - 7, dk(slab, 0.15));
  disc(g, cx, cy, R - 18, R - 18, slab); disc(g, cx, cy, R - 20, R - 20, dk(slab, 0.12));
  for (let k = 0; k < 24; k++) {
    const a = k / 24 * Math.PI * 2, lx = Math.round(cx + Math.cos(a) * (R - 12)), ly = Math.round(cy + Math.sin(a) * (R - 12));
    px(g, lx - 2, ly - 2, 5, 5, k % 2 ? K.warmHi : K.warm);
  }
  // the H: two uprights and a crossbar, with a pink-white rim line so it reads as painted and lit
  const hw = 78, hh = 104, bar = 22;
  const hx = cx - hw / 2, hy = cy - hh / 2;
  px(g, hx - 2, hy - 2, bar + 4, hh + 4, K.ink); px(g, hx + hw - bar - 2, hy - 2, bar + 4, hh + 4, K.ink); px(g, hx + bar - 2, cy - 10 - 2, hw - 2 * bar + 4, 24, K.ink);
  px(g, hx, hy, bar, hh, K.white); px(g, hx + hw - bar, hy, bar, hh, K.white); px(g, hx + bar, cy - 10, hw - 2 * bar, 20, K.white);
  px(g, hx, hy, 3, hh, lt(K.white, 0.4)); px(g, hx + hw - bar, hy, 3, hh, lt(K.white, 0.4));
  px(g, hx + bar, cy + 6, hw - 2 * bar, 4, K.paintLo);
  speckle(g, hx - 4, hy - 4, hw + 8, hh + 8, K.paintLo, 0.08, 43); // worn paint
  // corner marker lights and edge lights, baked glow on the slab
  for (const [lx, ly] of [[x + 20, y + 20], [x + w - 20, y + 20], [x + 20, y + h - 20], [x + w - 20, y + h - 20]]) {
    disc(g, lx, ly, 7, 7, K.ink); disc(g, lx, ly, 5, 5, K.red); disc(g, lx - 1, ly - 1, 2, 2, K.warmHi); L(lx - 14, ly - 14, 28, 28, K.pink, 0.55, false);
  }
  for (let i = 3; i < 13; i += 2) for (const ly of [y + 24, y + h - 24]) { px(g, x + i * HT, ly - 2, 5, 5, K.warmHi); L(x + i * HT - 6, ly - 8, 17, 17, K.amber, 0.28); }
  for (let j = 2; j < 11; j += 2) for (const lx of [x + 24, x + w - 24]) { px(g, lx - 2, y + j * HT, 5, 5, K.warmHi); L(lx - 8, y + j * HT - 6, 17, 17, K.amber, 0.28); }
  L(cx - R, cy - R, R * 2, R * 2, K.amber, 0.12);
  // the ramp: yellow-black hazard chevrons run from the pad edge out over the gap tile
  px(g, rampX, y + h - HT, rampW, HT, dk(K.ink, 0.1));
  for (let k = 0; k < 4; k++) {
    const ry = y + h - HT + k * 8;
    for (let i = 0; i < rampW; i += 16) { px(g, rampX + i, ry, 8, 4, K.warm); px(g, rampX + i + 8, ry + 4, 8, 4, K.warm); }
  }
  px(g, rampX, y + h + 2, rampW, 3, K.warm); px(g, rampX - 4, y + h - HT, 4, HT * 2, K.ink); px(g, rampX + rampW, y + h - HT, 4, HT * 2, K.ink);
  L(rampX, y + h + 4, rampW, 10, K.amber, 0.5, true);
}

/** a chain-link fence span (a low wall tile run): concrete footing, mesh, posts and a top rail */
export function helipadFence(g: G, x: number, y: number, w: number, h: number) {
  const horiz = w >= h;
  shadow(g, x + 3, y + h - 4, w - 2, 7, 0.35);
  px(g, x, y + h - 10, w, 10, K.ink); px(g, x + 1, y + h - 9, w - 2, 5, K.concreteHi); px(g, x + 1, y + h - 4, w - 2, 2, K.concreteLo);
  const top = y + 6, mh = h - 16;
  px(g, x, top, w, mh, alpha(K.ink, 0.55));
  for (let i = 0; i < w; i += 4) for (let j = 0; j < mh; j += 4) if (((i + j) >> 2) % 2 === 0) px(g, x + i, top + j, 2, 2, alpha(K.metalHi, 0.5));
  px(g, x, top, w, 3, K.metalHi); px(g, x, top + 3, w, 1, K.ink);
  const n = horiz ? w : h, step = HT;
  for (let i = 0; i <= n; i += step) {
    const px0 = horiz ? x + Math.min(i, w - 4) : x + 14;
    if (horiz) { px(g, px0, top - 3, 4, mh + 5, K.metal); px(g, px0, top - 3, 1, mh + 5, K.metalHi); }
  }
  if (!horiz) { px(g, x + 12, y, 8, h, K.metalLo); px(g, x + 14, y, 4, h, alpha(K.metalHi, 0.6)); for (let j = 0; j < h; j += 16) px(g, x + 11, y + j, 10, 2, K.metalHi); }
  // red-white warning tabs along the top
  if (horiz) for (let i = 8; i + 8 < w; i += 64) { px(g, x + i, top + 6, 16, 8, K.red); px(g, x + i + 2, top + 8, 12, 2, K.white); }
}

/** a windsock on a tall pole: striped sleeve streaming east */
export function windsock() {
  const [cv, g] = canvas(64, 110), ax = 20, ay = 108;
  shadow(g, ax - 10, ay - 3, 22, 6, 0.4);
  px(g, ax - 6, ay - 8, 13, 8, K.ink); px(g, ax - 5, ay - 7, 11, 5, K.concrete);
  px(g, ax - 2, 18, 5, ay - 24, K.ink); px(g, ax - 1, 18, 3, ay - 24, K.metal); px(g, ax - 1, 18, 1, ay - 24, K.metalHi);
  px(g, ax - 5, 12, 12, 5, K.ink); px(g, ax - 4, 13, 10, 3, K.metalHi); // ring
  const cols = [K.red, K.white, K.red, K.white, K.red];
  for (let i = 0; i < 5; i++) { const sx = ax + 6 + i * 7, sh = 17 - i * 2; px(g, sx, 11 - (sh - 10) / 2 - 2, 8, sh + 4, K.ink); px(g, sx, 12 - (sh - 10) / 2 - 2 + 1, 7, sh + 2, cols[i]); }
  disc(g, ax, 10, 3, 3, K.red);
  return { cv, ax, ay };
}

/** a red obstruction beacon on a mast, with a lit lamp head */
export function beacon() {
  const [cv, g] = canvas(32, 90), ax = 16, ay = 88;
  shadow(g, ax - 8, ay - 3, 17, 5, 0.4);
  px(g, ax - 6, ay - 8, 13, 8, K.ink); px(g, ax - 5, ay - 7, 11, 5, K.concrete);
  px(g, ax - 2, 14, 5, ay - 20, K.ink); px(g, ax - 1, 14, 3, ay - 20, K.metalLo);
  for (let j = 24; j < ay - 14; j += 12) { px(g, ax - 2, j, 5, 6, K.red); px(g, ax - 2, j + 6, 5, 6, K.white); }
  disc(g, ax, 9, 7, 7, K.ink); disc(g, ax, 9, 5, 5, K.red); disc(g, ax - 1, 8, 2, 2, mix(K.pink, K.white, 0.6));
  return { cv, ax, ay };
}
